import { ClipboardDocumentIcon, EllipsisVerticalIcon, EyeIcon, PlusIcon, TrashIcon } from '@heroicons/react/20/solid'
import { FolderPlusIcon } from '@heroicons/react/24/outline'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, useParams } from 'react-router'
import { Head } from '#/components/head'
import { EmptyState } from '#/components/empty-state'
import { Button } from '#/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '#/components/ui/dropdown-menu'
import { Separator } from '#/components/ui/separator'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '#/components/ui/table'
import { Heading, Subheading, Text } from '#/components/ui/typography'
import { copy } from '#/lib/copy'
import { formatDateTime2Digit } from '#/lib/datetime'
import { personalKeycardAPI } from '#/api/personalKeycard'
import { DeletePersonalKeycardModal } from './@modal/delete/page'
import type { PersonalKeycard } from 'cleverev-types'
import { toast } from 'sonner'


export default function PersonalKeycardsPage() {
  const { t, i18n } = useTranslation([ 'personal_keycards' ])
  const params = useParams() as { organization: string }
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState<{ personalKeycardId: string } | null>(null)

  // Fetch personal keycards
  const { data: { personalKeycards, totalCount } } = useSuspenseQuery({
    queryKey: [ 'organizations', params.organization, 'personalKeycards' ],
    queryFn: () => personalKeycardAPI.listpersonalKeycards(params.organization)
  })

  // Copy personal keycard id and notify
  function copyPersonalKeycardId(personalKeycard: PersonalKeycard) {
    copy(personalKeycard.id)
    toast.info(t('view_personal_keycard.id_copied'))
  }

  return (
    <div className="flex grow flex-col gap-4 items-start">

      <Head title={t('personal_keycard_list.page_title')} />

      <Outlet />

      <div className="flex w-full items-end justify-between gap-4">
        <Heading>{t('personal_keycard_list.header_title')}</Heading>
        <Link to="./new">
          <Button>
            <PlusIcon />
            {t('personal_keycard_list.action_new_personal_keycard')}
          </Button>
        </Link>
      </div>

      <Text>{t('personal_keycard_list.header_description')}</Text>

      <Separator />

      {totalCount === 0 ? (
        <EmptyState
          title={t('personal_keycard_list.empty_title')}
          description={t('personal_keycard_list.empty_description')}
          Icon={FolderPlusIcon}
        >
          <Link to="./new">
            <Button>
              <PlusIcon />
              {t('personal_keycard_list.action_new_personal_keycard')}
            </Button>
          </Link>
        </EmptyState>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('fields.id')}</TableHead>
                <TableHead>{t('fields.hardwareId')}</TableHead>
                <TableHead>{t('fields.friendlyId')}</TableHead>
                <TableHead>{t('fields.created_at')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isDeleteModalOpen !== null
              && (<DeletePersonalKeycardModal
                id={isDeleteModalOpen.personalKeycardId}
                onClose={() => setIsDeleteModalOpen(null)}
              />)}
              {personalKeycards.map(personalKeycardData => (
                <TableRow key={personalKeycardData.id}>
                  <TableCell className="font-medium">
                    <Link to={`./${personalKeycardData.id}`} state={{ name: personalKeycardData.id }}>
                      <Subheading>{personalKeycardData.id}</Subheading>
                    </Link>
                  </TableCell>
                  <TableCell>
                    {personalKeycardData.hardwareId}
                  </TableCell>
                  <TableCell>
                    {personalKeycardData.friendlyId}
                  </TableCell>
                  <TableCell>
                    {formatDateTime2Digit(personalKeycardData.createdAt, i18n.language)}
                  </TableCell>
                  <TableCell className="text-right flex flex-row justify-end">
                    <Link to={`./${personalKeycardData.id}`} state={{ name: personalKeycardData.id }}>
                      <Button variant="ghost" className="mr-4">
                        {t('personal_keycard_list.view_button')}
                      </Button>
                    </Link>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <EllipsisVerticalIcon />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuLabel>{t('personal_keycard_list.menu.label')}</DropdownMenuLabel>
                        <Link to={`./${personalKeycardData.id}`} state={{ name: personalKeycardData.id }}>
                          <DropdownMenuItem>
                            <EyeIcon className="text-tertiary" />
                            {t('personal_keycard_list.menu.view')}
                          </DropdownMenuItem>
                        </Link>
                        <DropdownMenuItem onClick={() => copyPersonalKeycardId(personalKeycardData)}>
                          <ClipboardDocumentIcon className="text-tertiary" />
                          {t('personal_keycard_list.menu.copy_id')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() =>
                          setIsDeleteModalOpen({
                            personalKeycardId: personalKeycardData.id
                          })}>
                          <TrashIcon className="text-tertiary" />
                          {t('personal_keycard_list.menu.remove')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

    </div>
  )
}
